<template>
  <div>
    <v-col cols="12">
      <div style="min-height: 4px">
        <v-progress-linear
          v-if="loading || saving"
          indeterminate
        />
      </div>

      <v-card>
        <v-card-title
          class="d-flex justify-space-between align-center py-3 elevation-1"
          style="z-index: 2; position: relative;"
        >
          <span class="text-h6">Editor de Template</span>
          <div>
            <v-btn
              color="secondary"
              outlined
              class="mr-2"
              :disabled="saving"
              @click="cancelar"
            >
              Voltar
            </v-btn>
            <v-btn
              color="success"
              :loading="saving"
              @click="salvarTudo"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              Salvar Template
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-form
            ref="formRef"
            v-model="valid"
          >
            <v-row no-gutters>
              <v-col
                cols="12"
                md="3"
                class="border-right pa-4"
                style="height: 85vh; overflow-y: auto;"
              >
                <h3 class="text-subtitle-1 font-weight-bold mb-4 primary--text">
                  Configurações
                </h3>

                <v-text-field
                  v-model="templateData.nome"
                  outlined
                  dense
                  label="Nome do Template"
                  :rules="[validators.required]"
                  placeholder="Ex: Promoção de Natal"
                  class="mb-2"
                ></v-text-field>

                <v-textarea
                  v-model="templateData.descricao"
                  outlined
                  dense
                  rows="4"
                  label="Descrição Interna"
                  placeholder="Para que serve este email?"
                  class="mb-2"
                ></v-textarea>

                <v-card
                  outlined
                  class="pa-3 mb-4 rounded-lg"
                >
                  <v-checkbox
                    v-model="templateData.envioEmMassa"
                    label="Permitir Envio em Massa"
                    hide-details
                    class="mt-0"
                  ></v-checkbox>
                  <small
                    class="grey--text ml-7 d-block mt-1"
                    style="line-height: 1.2;"
                  >
                    Disponibiliza este template na tela de disparos em lote.
                  </small>
                </v-card>

                <v-alert
                  type="info"
                  dense
                  text
                  small
                  class="mt-4"
                  border="left"
                >
                  Dica: Use as <strong>Variáveis (Merge Tags)</strong> no editor ao lado para inserir o nome do cliente automaticamente.
                </v-alert>
              </v-col>

              <v-col
                cols="12"
                md="9"
                class="d-flex flex-column"
              >
                <div
                  class="editor-wrapper"
                  style="height: 85vh; width: 100%; position: relative; overflow: hidden;"
                >
                  <EmailEditor
                    ref="emailEditor"
                    min-height="100%"
                    style="width: 100%; height: 100%;"
                    :locale="'pt-BR'"
                    :options="editorOptions"
                    @load="editorLoaded"
                  />
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </div>
</template>

<script>
import Vue from 'vue'
import { ref, onMounted } from '@vue/composition-api'
import { required } from '@core/utils/validation'
import { EmailEditor } from 'vue-email-editor'
import router from '@/router'
import TemplateEmailService from '@/services/modules/TemplateEmailService'
import TemplatePadrao from '@/components/email-editor/TemplatePadraoComercial'

export default {
  components: { EmailEditor },

  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const valid = ref(false)
    const formRef = ref(null)
    const emailEditor = ref(null)

    const templateData = ref({
      id: null,
      nome: '',
      descricao: '',
      envioEmMassa: false,
      conteudoJson: null,
      conteudoHtml: null,
    })

    // --- CONFIGURAÇÃO DO EDITOR ---
    const editorOptions = ref({
      appearance: {
        theme: 'modern',
        panels: {
          tools: {
            dock: 'right', // Ferramentas na direita
          },
        },
      },
      // Variáveis (Merge Tags)
      mergeTags: {
        nome_cliente: {
          name: 'Nome do Cliente',
          value: '{{nome_cliente}}',
        },
        email_cliente: {
          name: 'Email do Cliente',
          value: '{{email_cliente}}',
        },
        protocolo_atendimento: {
          name: 'Protocolo',
          value: '{{protocolo}}',
        },
        nome_agente: {
          name: 'Nome do Atendente',
          value: '{{nome_agente}}',
        },
      },
    })

    // --- CARREGAMENTO ---
    async function loadData() {
      const { id } = router.currentRoute.params
      if (!id) return

      loading.value = true
      try {
        const data = await TemplateEmailService.getById(id)
        templateData.value = data
      } catch (error) {
        console.error(error)
        Vue.$toast.error('Erro ao carregar dados do template')
      } finally {
        loading.value = false
      }
    }

    function editorLoaded() {
      if (templateData.value.conteudoJson) {
        let design = templateData.value.conteudoJson
        if (typeof design === 'string') design = JSON.parse(design)
        emailEditor.value.editor.loadDesign(design)
      } else {
        emailEditor.value.editor.loadDesign(TemplatePadrao)
      }
    }

    // --- SALVAMENTO ---
    const exportDataAsync = () => new Promise(resolve => {
      emailEditor.value.editor.exportHtml(data => {
        resolve(data)
      })
    })

    async function salvarTudo() {
      if (!formRef.value.validate()) {
        Vue.$toast.warning('Preencha o nome do template')
        return
      }

      saving.value = true

      try {
        const editorResult = await exportDataAsync()

        const payload = {
          ...templateData.value,
          conteudoHtml: editorResult.html,
          conteudoJson: JSON.stringify(editorResult.design),
        }

        if (payload.id) {
          await TemplateEmailService.atualizar(payload.id, payload)
        } else {
          // await TemplateEmailService.criar(payload)
        }

        Vue.$toast.success('Template salvo com sucesso!')
      } catch (error) {
        console.error(error)
        Vue.$toast.error('Erro ao salvar template')
      } finally {
        saving.value = false
      }
    }

    function cancelar() {
      router.back()
    }

    onMounted(() => {
      loadData()
    })

    return {
      loading,
      saving,
      valid,
      formRef,
      emailEditor,
      templateData,
      editorOptions,
      editorLoaded,
      salvarTudo,
      cancelar,
      validators: { required },
    }
  },
}
</script>

<style scoped>
.border-right {
  border-right: 1px solid #e0e0e0;
}
/* Responsividade mobile */
@media (max-width: 960px) {
  .border-right {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    height: auto !important;
  }
  .editor-wrapper {
    height: 600px !important;
  }
}
</style>
